import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Options {
  active: boolean;
  anuncioId: string;
  sessionContato?: string;
  sessionToken?: string;
  onConfirmed?: () => void;
}

/**
 * Polls client-anuncios every 2s checking if `taxa_paga` became true for the
 * given ad. When detected, sets `confirmed=true` for 2s, fires the callback
 * and shows a toast. Used by inline QR overlays in cards/list/carousel.
 */
export function useAnuncioPaymentPolling({
  active,
  anuncioId,
  sessionContato,
  sessionToken,
  onConfirmed,
}: Options) {
  const [confirmed, setConfirmed] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!active || !anuncioId || !sessionContato || !sessionToken) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setConfirmed(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = 150; // 5 minutes @ 2s

    const poll = async () => {
      attempts++;
      if (attempts > maxAttempts) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }
      try {
        const { data } = await supabase.functions.invoke("client-anuncios", {
          body: { contato: sessionContato, token: sessionToken },
        });
        const anuncios = data?.anuncios || data || [];
        const found = Array.isArray(anuncios)
          ? anuncios.find((a: any) => a.id === anuncioId)
          : null;
        if (found?.taxa_paga) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setConfirmed(true);
          setTimeout(() => {
            setConfirmed(false);
            toast({
              title: "✅ Pagamento confirmado!",
              description: "Seu anúncio está sendo processado.",
            });
            onConfirmed?.();
          }, 2000);
        }
      } catch {
        /* ignore */
      }
    };

    poll();
    intervalRef.current = setInterval(poll, 2000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, anuncioId, sessionContato, sessionToken]);

  return { confirmed };
}
